<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CollapsibleSection from './CollapsibleSection.vue';
import TabTableOfContents from './TabTableOfContents.vue';
import ProductGeneralImageSection from './ProductGeneralImageSection.vue';
import ProductGalleryPanel from './ProductGalleryPanel.vue';
import ProductGeneralCategoriesSection, {
  type Category,
} from './ProductGeneralCategoriesSection.vue';
import './epp-shared.css';

const { t } = useI18n({ useScope: 'global' });

/* ── Image (parent owns the File/upload state, no v-model on a File input) ── */
const props = defineProps<{
  productId: string | null;
  displayImg: string | null;
  existingImgUrl: string | null;
  imgFile: File | null;

  attemptedSubmit: boolean;
  transName: boolean;
  transDesc: boolean;

  categories: Category[];
  loadingCats: boolean;
  selectedCats: string[];
  showCatForm: boolean;
  editCatId: string | null;
  catSaving: boolean;
  catError: string | null;
  transNameCat: boolean;
  transDescCat: boolean;
  deletingCatId: string | null;
  catLabel: (c: Category) => string;
}>();

const emit = defineEmits<{
  imgChange: [e: Event];
  imgRemove: [];
  nameBlur: [lang: 'fr' | 'en' | 'nl'];
  descBlur: [lang: 'fr' | 'en' | 'nl'];
  openNewCat: [];
  openEditCat: [cat: Category];
  closeCatForm: [];
  saveCat: [];
  deleteCat: [cat: Category];
  toggleCat: [id: string];
  catNameBlur: [lang: 'fr' | 'en' | 'nl'];
  catDescBlur: [lang: 'fr' | 'en' | 'nl'];
}>();

/* ── Two-way simple fields: defineModel (Vue 3.5, no per-field emit plumbing) ── */
const nameLang = defineModel<'fr' | 'en' | 'nl'>('nameLang', {
  required: true,
});
const nameFr = defineModel<string>('nameFr', { required: true });
const nameEn = defineModel<string>('nameEn', { required: true });
const nameNl = defineModel<string>('nameNl', { required: true });

const descLang = defineModel<'fr' | 'en' | 'nl'>('descLang', {
  required: true,
});
const descFr = defineModel<string>('descFr', { required: true });
const descEn = defineModel<string>('descEn', { required: true });
const descNl = defineModel<string>('descNl', { required: true });

const catFormLang = defineModel<'fr' | 'en' | 'nl'>('catFormLang', {
  required: true,
});
const catNameFr = defineModel<string>('catNameFr', { required: true });
const catNameEn = defineModel<string>('catNameEn', { required: true });
const catNameNl = defineModel<string>('catNameNl', { required: true });
const catDescFr = defineModel<string>('catDescFr', { required: true });
const catDescEn = defineModel<string>('catDescEn', { required: true });
const catDescNl = defineModel<string>('catDescNl', { required: true });
const catModules = defineModel<string[]>('catModules', { required: true });

const showNameFrError = computed(
  () => props.attemptedSubmit && !nameFr.value.trim(),
);
const showNameEnError = computed(
  () => props.attemptedSubmit && !nameEn.value.trim(),
);
const showNameNlError = computed(
  () => props.attemptedSubmit && !nameNl.value.trim(),
);
const showCatNameFrError = computed(() => !catNameFr.value.trim());

/* ── Table of contents + collapsible-open control ────────────────────────── */
const sections = [
  { key: 'image', label: 'Image' },
  ...(props.productId ? [{ key: 'gallery', label: 'Galerie' }] : []),
  { key: 'designation', label: t('product.catDesignation') },
  { key: 'description', label: t('product.descriptionOptional') },
  { key: 'categories', label: t('product.categoriesTitle') },
];

const imageSectionRef = ref<InstanceType<typeof CollapsibleSection> | null>(
  null,
);
const gallerySectionRef = ref<InstanceType<typeof CollapsibleSection> | null>(
  null,
);
const designationSectionRef = ref<InstanceType<
  typeof CollapsibleSection
> | null>(null);
const descriptionSectionRef = ref<InstanceType<
  typeof CollapsibleSection
> | null>(null);
const categoriesSectionRef = ref<InstanceType<
  typeof CollapsibleSection
> | null>(null);

const sectionRefs: Record<string, typeof imageSectionRef> = {
  image: imageSectionRef,
  gallery: gallerySectionRef,
  designation: designationSectionRef,
  description: descriptionSectionRef,
  categories: categoriesSectionRef,
};

function onJump(key: string) {
  const target = sectionRefs[key]?.value;
  if (target) target.isOpen = true;
  document
    .getElementById(`section-${key}`)
    ?.scrollIntoView({ behavior: 'smooth' });
}
</script>

<template>
  <div class="epp__general-tab">
    <TabTableOfContents :sections="sections" @jump="onJump" />

    <CollapsibleSection
      ref="imageSectionRef"
      section-key="image"
      title="Image"
      :subtitle="t('product.keepCurrentImageHint')"
      icon="pi-image"
      default-open
    >
      <ProductGeneralImageSection
        :display-img="displayImg"
        :existing-img-url="existingImgUrl"
        :img-file="imgFile"
        @change="emit('imgChange', $event)"
        @remove="emit('imgRemove')"
      />
    </CollapsibleSection>

    <!-- Galerie d'images secondaires — seulement sur un produit déjà créé,
     * les endpoints de galerie visent un productId existant. -->
    <CollapsibleSection
      v-if="productId"
      ref="gallerySectionRef"
      section-key="gallery"
      title="Galerie"
      subtitle="Images secondaires affichées en plus de l'image principale."
      icon="pi-images"
    >
      <ProductGalleryPanel :product-id="productId" />
    </CollapsibleSection>

    <CollapsibleSection
      ref="designationSectionRef"
      section-key="designation"
      icon="pi-tag"
      :title="`${t('product.catDesignation')} *`"
      :subtitle="t('product.designationSubtitle')"
    >
      <div class="epp__fields">
        <div class="epp__translabel-row">
          <label class="epp__label">{{ t('product.inputLang') }}</label>
          <div class="epp__select-wrap" style="max-width: 10rem">
            <select v-model="nameLang" class="epp__select epp__select--sm">
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="nl">Nederlands</option>
            </select>
            <i class="pi pi-chevron-down epp__select-icon" />
          </div>
        </div>
        <div class="epp__field-row epp__field-row--3">
          <div class="epp__field">
            <label class="epp__label"
              ><span
                class="epp__lang-badge"
                :class="nameLang === 'fr' ? 'epp__lang-badge--active' : ''"
                >FR</span
              >
              Français<span
                v-if="transName && nameLang !== 'fr'"
                class="epp__translating-dot"
            /></label>
            <input
              v-model="nameFr"
              class="epp__input"
              :class="[
                nameLang === 'fr' ? 'epp__input--primary' : '',
                { 'epp__input--error': showNameFrError },
              ]"
              type="text"
              :placeholder="t('product.nameFrPlaceholder')"
              @blur="emit('nameBlur', 'fr')"
            />
            <p v-if="showNameFrError" class="epp__field-error">
              {{ t('product.requiredField') }}
            </p>
          </div>
          <div class="epp__field">
            <label class="epp__label"
              ><span
                class="epp__lang-badge"
                :class="nameLang === 'en' ? 'epp__lang-badge--active' : ''"
                >EN</span
              >
              English<span
                v-if="transName && nameLang !== 'en'"
                class="epp__translating-dot"
            /></label>
            <input
              v-model="nameEn"
              class="epp__input"
              :class="[
                nameLang === 'en' ? 'epp__input--primary' : '',
                { 'epp__input--error': showNameEnError },
              ]"
              type="text"
              placeholder="Name in English…"
              @blur="emit('nameBlur', 'en')"
            />
            <p v-if="showNameEnError" class="epp__field-error">
              {{ t('product.requiredField') }}
            </p>
          </div>
          <div class="epp__field">
            <label class="epp__label"
              ><span
                class="epp__lang-badge"
                :class="nameLang === 'nl' ? 'epp__lang-badge--active' : ''"
                >NL</span
              >
              Nederlands<span
                v-if="transName && nameLang !== 'nl'"
                class="epp__translating-dot"
            /></label>
            <input
              v-model="nameNl"
              class="epp__input"
              :class="[
                nameLang === 'nl' ? 'epp__input--primary' : '',
                { 'epp__input--error': showNameNlError },
              ]"
              type="text"
              placeholder="Naam…"
              @blur="emit('nameBlur', 'nl')"
            />
            <p v-if="showNameNlError" class="epp__field-error">
              {{ t('product.requiredField') }}
            </p>
          </div>
        </div>
      </div>
    </CollapsibleSection>

    <CollapsibleSection
      ref="descriptionSectionRef"
      section-key="description"
      icon="pi-align-left"
      :title="t('product.descriptionOptional')"
      :subtitle="t('product.descriptionSubtitle')"
    >
      <div class="epp__fields">
        <div class="epp__translabel-row">
          <label class="epp__label">{{ t('product.inputLang') }}</label>
          <div class="epp__select-wrap" style="max-width: 10rem">
            <select v-model="descLang" class="epp__select epp__select--sm">
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="nl">Nederlands</option>
            </select>
            <i class="pi pi-chevron-down epp__select-icon" />
          </div>
        </div>
        <div class="epp__field-row epp__field-row--3">
          <div class="epp__field">
            <label class="epp__label"
              ><span
                class="epp__lang-badge"
                :class="descLang === 'fr' ? 'epp__lang-badge--active' : ''"
                >FR</span
              ><span
                v-if="transDesc && descLang !== 'fr'"
                class="epp__translating-dot"
            /></label>
            <textarea
              v-model="descFr"
              class="epp__textarea"
              :class="descLang === 'fr' ? 'epp__textarea--primary' : ''"
              rows="3"
              :placeholder="t('product.descFrAreaPlaceholder')"
              @blur="emit('descBlur', 'fr')"
            />
          </div>
          <div class="epp__field">
            <label class="epp__label"
              ><span
                class="epp__lang-badge"
                :class="descLang === 'en' ? 'epp__lang-badge--active' : ''"
                >EN</span
              ><span
                v-if="transDesc && descLang !== 'en'"
                class="epp__translating-dot"
            /></label>
            <textarea
              v-model="descEn"
              class="epp__textarea"
              :class="descLang === 'en' ? 'epp__textarea--primary' : ''"
              rows="3"
              placeholder="Description in English…"
              @blur="emit('descBlur', 'en')"
            />
          </div>
          <div class="epp__field">
            <label class="epp__label"
              ><span
                class="epp__lang-badge"
                :class="descLang === 'nl' ? 'epp__lang-badge--active' : ''"
                >NL</span
              ><span
                v-if="transDesc && descLang !== 'nl'"
                class="epp__translating-dot"
            /></label>
            <textarea
              v-model="descNl"
              class="epp__textarea"
              :class="descLang === 'nl' ? 'epp__textarea--primary' : ''"
              rows="3"
              placeholder="Beschrijving…"
              @blur="emit('descBlur', 'nl')"
            />
          </div>
        </div>
      </div>
    </CollapsibleSection>

    <CollapsibleSection
      ref="categoriesSectionRef"
      section-key="categories"
      icon="pi-folder"
      :title="t('product.categoriesTitle')"
      :subtitle="t('product.categoriesSubtitle')"
    >
      <ProductGeneralCategoriesSection
        :categories="categories"
        :loading-cats="loadingCats"
        :selected-cats="selectedCats"
        :show-cat-form="showCatForm"
        :edit-cat-id="editCatId"
        v-model:cat-form-lang="catFormLang"
        v-model:cat-name-fr="catNameFr"
        v-model:cat-name-en="catNameEn"
        v-model:cat-name-nl="catNameNl"
        v-model:cat-desc-fr="catDescFr"
        v-model:cat-desc-en="catDescEn"
        v-model:cat-desc-nl="catDescNl"
        v-model:cat-modules="catModules"
        :cat-saving="catSaving"
        :cat-error="catError"
        :show-cat-name-fr-error="showCatNameFrError"
        :trans-name-cat="transNameCat"
        :trans-desc-cat="transDescCat"
        :deleting-cat-id="deletingCatId"
        :cat-label="catLabel"
        @open-new-cat="emit('openNewCat')"
        @open-edit-cat="(cat) => emit('openEditCat', cat)"
        @close-cat-form="emit('closeCatForm')"
        @save-cat="emit('saveCat')"
        @delete-cat="(cat) => emit('deleteCat', cat)"
        @toggle-cat="(id) => emit('toggleCat', id)"
        @cat-name-blur="(lang) => emit('catNameBlur', lang)"
        @cat-desc-blur="(lang) => emit('catDescBlur', lang)"
      />
    </CollapsibleSection>
  </div>
</template>
